import React, { createContext, useContext, useState } from 'react';
import ConfirmDialog from '../components/dialogs/ConfirmDialog';
import AlertDialog from '../components/dialogs/AlertDialog';

interface ConfirmOptions {
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: string;
}

interface AlertOptions {
  title?: string;
  message: string;
  buttonText?: string;
  variant?: string;
}

export interface DialogContextValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  alert: (options: AlertOptions) => Promise<void>;
}

export const DialogContext = createContext<DialogContextValue | null>(null);

export function useDialog(): DialogContextValue {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within DialogProvider');
  }
  return context;
}

export function DialogProvider({ children }: { children: React.ReactNode }) {
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    variant: 'danger',
    onConfirm: () => {},
  });

  const [alertState, setAlertState] = useState({
    isOpen: false,
    title: '',
    message: '',
    buttonText: 'OK',
    variant: 'info',
  });

  const resolveRef = React.useRef<((value: boolean) => void) | null>(null);

  const confirm = ({ 
    title = 'Confirm Action', 
    message = 'Are you sure?',
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    variant = 'danger'
  }: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setConfirmState({
        isOpen: true,
        title,
        message,
        confirmText,
        cancelText,
        variant,
        onConfirm: () => {
          resolve(true);
          resolveRef.current = null;
          setConfirmState(prev => ({ ...prev, isOpen: false }));
        },
      });
    });
  };

  const alert = ({ 
    title = 'Alert', 
    message,
    buttonText = 'OK',
    variant = 'info'
  }) => {
    return new Promise<void>((resolve) => {
      setAlertState({
        isOpen: true,
        title,
        message,
        buttonText,
        variant,
      });
      resolve();
    });
  };

  const closeConfirm = () => {
    if (resolveRef.current) {
      resolveRef.current(false);
      resolveRef.current = null;
    }
    setConfirmState(prev => ({ ...prev, isOpen: false }));
  };

  const closeAlert = () => {
    setAlertState(prev => ({ ...prev, isOpen: false }));
  };

  return (
    <DialogContext.Provider value={{ confirm, alert } as DialogContextValue}>
      {children}
      
      <ConfirmDialog
        isOpen={confirmState.isOpen}
        onClose={closeConfirm}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        variant={confirmState.variant}
      />
      
      <AlertDialog
        isOpen={alertState.isOpen}
        onClose={closeAlert}
        title={alertState.title}
        message={alertState.message}
        buttonText={alertState.buttonText}
        variant={alertState.variant}
      />
    </DialogContext.Provider>
  );
}
